import {
  Autocomplete,
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { styled } from '@mui/material/styles';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import Loading from '../../components/Loading';
import { useAppDispatch } from '../../store/hooks';
import { useAddRoleMutation, useFindRoleByIdMutation, useUpdateRoleMutation } from '../../store/api/roleApi';
import { setUserRole } from '../../store/state/roleSlice';

type Props = {};

const customerSchema = Yup.object().shape({
  name: Yup.string()
    .required('Please enter full name')
    .matches(/^[a-zA-Z\s.-]+$/, { message: 'Name must be alphabetic' }),
  phoneNo: Yup.string()
    .required('Phone number is required')
    .min(10, 'Minimum 10 digits allowed')
    .max(10, 'Maximum 10 digits allowed'),
  email: Yup.string().email('Invalid email').required('Email is required'),
});

const ResponsiveBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const FormikBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: '20px',
  },
}));

const AddRole = (props: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [addRole, addRoleResponse] = useAddRoleMutation();
  const [findRoleById, findRoleByIdResponse] = useFindRoleByIdMutation();
  const [updateRole, updateRoleResponse] = useUpdateRoleMutation();
  const [initialValues, setInitialValues] = useState({
    roleName: '',
    role: 0,
    permission: '',
  });
  const { state }: any = useLocation();
  const token = JSON.parse(localStorage.getItem('login') || '{}').token;

  useEffect(() => {
    if (addRoleResponse.isSuccess) {
      dispatch(setUserRole(addRoleResponse.data));
      navigate('/dashboard/role-management');
    }
    if (addRoleResponse.isError) {
      let errorData: any = addRoleResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [addRoleResponse.isSuccess, addRoleResponse.isError]);

  useEffect(() => {
    if (state.editMode || state.isApproved) {
      findRoleById({ id: state.id, token: token.token });
    }
  }, [state.editMode, state.isApproved]);

  useEffect(() => {
    if (findRoleByIdResponse.isSuccess) {
      setInitialValues(() => {
        return {
          roleName: findRoleByIdResponse.data.roleName,
          role: findRoleByIdResponse.data.role,
          permission: findRoleByIdResponse.data.role.permission,
        };
      });
    }

    if (findRoleByIdResponse.isError) {
      let errorData: any = findRoleByIdResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [findRoleByIdResponse.isSuccess, findRoleByIdResponse.isError]);

  useEffect(() => {
    if (addRoleResponse.isSuccess || updateRoleResponse.isSuccess) {
      findRoleById({ id: state.id, token: token.token });
    }
    if (addRoleResponse.isError || updateRoleResponse.isError) {
      let errorData: any = addRoleResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [addRoleResponse.isSuccess, updateRoleResponse.isSuccess, addRoleResponse.isError, updateRoleResponse.isError]);

  const handleAddNewServiceProvider = (values: any, action: any) => {
    if (state.editMode) {
      updateRole({
        roleName: values.roleName,
        role: values.role,
        permission: values.permission,
      });
      toast.success('User Role updated successfully');
    } else {
      addRole({
        payload: {
          roleName: values.roleName,
          role: values.role,
          permission: values.permission,
        },
      });
      toast.success('User Role added successfully');
    }
    action.resetForm();
  };

  return (
    <Box>
      <Container>
        <h1>{state.editMode ? 'Edit User' : 'Add User'}</h1>
        <ResponsiveBox style={{ display: 'flex', paddingTop: '40px' }}>
          <FormikBox style={{ flex: 1, padding: 0 }}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleAddNewServiceProvider}
              validationSchema={customerSchema}
            >
              {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, touched, errors }) => (
                <Box>
                  <Box marginTop={'25px'} marginLeft={'25px'}>
                    <Typography variant="h5" gutterBottom component="div">
                      User Role Name
                    </Typography>
                    <Box>
                      <TextField
                        fullWidth
                        id="serviceProviderId"
                        onChange={handleChange}
                        value={values.roleName}
                        onBlur={handleBlur}
                        name="serviceProviderId"
                        style={{
                          width: '90%',
                        }}
                      />
                    </Box>
                  </Box>
                  <Box marginTop={'25px'} marginLeft={'25px'}>
                    <Typography variant="h5" gutterBottom component="div">
                      User Role
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel id="role">User Role</InputLabel>
                      <Select
                        labelId="Role"
                        id="role"
                        value={values.role}
                        style={{ width: '90%' }}
                        label="User Role"
                        onChange={handleChange}
                        displayEmpty
                      >
                        <MenuItem disabled value="">
                          Select User Role
                        </MenuItem>
                        <MenuItem value={10}>Vender</MenuItem>
                        <MenuItem value={20}>Admin</MenuItem>
                        <MenuItem value={30}>Customer</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box marginTop={'25px'} marginLeft={'25px'}>
                    <Typography variant="h5" gutterBottom component="div">
                      Permission
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel id="permission">Permission</InputLabel>
                      <Select
                        labelId="Permission"
                        id="Permission"
                        value={values.permission}
                        style={{ width: '90%' }}
                        label="Permission"
                        onChange={handleChange}
                        displayEmpty
                        defaultValue={''}
                      >
                        <MenuItem disabled value="">
                          Select permission
                        </MenuItem>
                        <MenuItem value={10}>Vender</MenuItem>
                        <MenuItem value={20}>Admin</MenuItem>
                        <MenuItem value={30}>Customer</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => {
                        navigate('/dashboard/role-management');
                      }}
                      sx={{ marginRight: '10px' }}
                      size="large"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        handleSubmit();
                      }}
                      sx={{ marginRight: '40px' }}
                      size="large"
                      type="submit"
                    >
                      {state.editMode ? 'Update' : 'Add'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Formik>
          </FormikBox>
        </ResponsiveBox>
      </Container>
      {addRoleResponse.isLoading || findRoleByIdResponse.isLoading || updateRoleResponse.isLoading ? <Loading /> : null}
    </Box>
  );
};

export default AddRole;
